import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { homeForRole, useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";

const PORTALS = [
  { role: "member", label: "Member", href: "/login" },
  { role: "coach", label: "Coach", href: "/coach-login" },
  { role: "admin", label: "Admin", href: "/admin-login" },
];

export function PortalHeader({ current }) {
  return (
    <header className="px-5 sm:px-10 h-16 border-b border-zinc-800/70 flex items-center justify-between">
      <Link to="/" data-testid="portal-home-link"><Wordmark className="text-2xl" /></Link>
      <nav className="flex items-center gap-2" aria-label="Login portals">
        {PORTALS.filter((p) => p.role !== current).map((p) => (
          <Link key={p.role} to={p.href} data-testid={`portal-link-${p.role}`}
            className="border border-zinc-700 px-3 py-2 text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-400 hover:border-volt hover:text-volt transition-colors">
            {p.label}
          </Link>
        ))}
      </nav>
    </header>
  );
}

export default function CredentialAuth({ role, eyebrow, title, description, allowRegister = false, allowGoogle = false, notice }) {
  const { user, setUser, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });

  if (user) return <Navigate to={user.must_change_password ? "/change-password" : homeForRole(user.role)} replace />;

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const finish = (account) => {
    setUser(account);
    navigate(account.must_change_password ? "/change-password" : homeForRole(account.role), { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, portal: role });
        toast.success(data.message);
        setMode("login");
      } else if (mode === "register") {
        if (form.password !== form.confirm) throw new Error("Passwords do not match");
        const { data } = await api.post("/auth/register", {
          name: form.name, email: form.email, phone: form.phone, password: form.password,
        });
        finish(data);
      } else {
        const { data } = await api.post(`/auth/${role}/login`, { email: form.email, password: form.password });
        finish(data);
      }
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const heading = mode === "register" ? "Create your account" : mode === "forgot" ? "Password help" : title;

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col" data-testid={`${role}-auth-page`}>
      <div className="grain-overlay" aria-hidden="true" />
      <PortalHeader current={role} />
      <main className="flex-1 grid lg:grid-cols-[1fr_520px]">
        <section className="hidden lg:flex flex-col justify-center px-16 border-r border-zinc-800/70">
          <p className="text-[10px] uppercase tracking-[0.3em] text-volt font-bold">{eyebrow}</p>
          <h1 className="font-display uppercase text-7xl leading-[0.85] mt-5">{title}</h1>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md mt-6">{description}</p>
          {notice && <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mt-10 max-w-md">{notice}</p>}
        </section>

        <section className="flex items-center justify-center px-5 py-12 sm:px-10">
          <form data-testid={`${role}-${mode}-form`} onSubmit={submit}
            className="w-full max-w-md border border-zinc-800 bg-zinc-950 p-7 sm:p-10">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{eyebrow}</p>
            <h2 className="font-display uppercase text-4xl leading-none mt-2">{heading}</h2>

            <div className="mt-8 space-y-4">
              {mode === "register" && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Full name</span>
                  <input data-testid="member-register-name" value={form.name} onChange={update("name")} required
                    className="mt-2 w-full bg-ink border border-zinc-700 px-4 py-4 text-sm focus:border-volt outline-none transition-colors" />
                </label>
              )}
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Email</span>
                <input data-testid={`${role}-email-input`} type="email" value={form.email} onChange={update("email")} required
                  autoComplete="email" className="mt-2 w-full bg-ink border border-zinc-700 px-4 py-4 text-sm focus:border-volt outline-none transition-colors" />
              </label>
              {mode === "register" && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Phone (optional)</span>
                  <input data-testid="member-register-phone" type="tel" value={form.phone} onChange={update("phone")}
                    autoComplete="tel" className="mt-2 w-full bg-ink border border-zinc-700 px-4 py-4 text-sm focus:border-volt outline-none transition-colors" />
                </label>
              )}
              {mode !== "forgot" && (
                <label className="block relative">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Password</span>
                  <input data-testid={`${role}-password-input`} type={showPassword ? "text" : "password"} value={form.password}
                    onChange={update("password")} required minLength={mode === "register" ? 10 : 1}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    className="mt-2 w-full bg-ink border border-zinc-700 px-4 py-4 pr-12 text-sm focus:border-volt outline-none transition-colors" />
                  <button type="button" data-testid={`${role}-password-toggle`} onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-[42px] text-zinc-500 hover:text-volt" aria-label="Toggle password visibility">
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </label>
              )}
              {mode === "register" && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Confirm password</span>
                  <input data-testid="member-confirm-password-input" type="password" value={form.confirm} onChange={update("confirm")} required
                    autoComplete="new-password" className="mt-2 w-full bg-ink border border-zinc-700 px-4 py-4 text-sm focus:border-volt outline-none transition-colors" />
                </label>
              )}
            </div>

            {error && <p data-testid={`${role}-auth-error`} className="mt-4 text-sm text-red-400">{error}</p>}

            <button data-testid={`${role}-${mode}-submit`} disabled={busy}
              className="mt-7 w-full bg-volt text-black uppercase tracking-[0.18em] text-xs font-extrabold py-4 hover:bg-white disabled:opacity-50 transition-colors">
              {busy ? "Please wait…" : mode === "register" ? "Create member account" : mode === "forgot" ? "Email secure link" : `Enter ${role} portal`}
            </button>

            {allowGoogle && mode === "login" && (
              <>
                <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-zinc-600">
                  <span className="h-px flex-1 bg-zinc-800" />or<span className="h-px flex-1 bg-zinc-800" />
                </div>
                <button type="button" data-testid={`${role}-google-login`} onClick={() => loginWithGoogle(role)}
                  className="w-full border border-zinc-700 text-white uppercase tracking-[0.18em] text-xs font-extrabold py-4 hover:border-volt hover:text-volt transition-colors">
                  Continue with Google
                </button>
              </>
            )}

            <div className="mt-6 flex flex-wrap justify-between gap-3 text-[11px] uppercase tracking-[0.14em] font-bold">
              {mode !== "forgot" ? (
                <button type="button" data-testid={`${role}-forgot-link`} onClick={() => setMode("forgot")}
                  className="text-zinc-500 hover:text-volt transition-colors">
                  {role === "member" ? "Forgot / first-time password" : "Forgot password"}
                </button>
              ) : (
                <button type="button" data-testid={`${role}-back-login`} onClick={() => setMode("login")}
                  className="inline-flex items-center gap-2 text-zinc-500 hover:text-volt transition-colors">
                  <ArrowLeft size={13} /> Back to login
                </button>
              )}
              {allowRegister && mode === "login" && (
                <button type="button" data-testid="member-show-register" onClick={() => setMode("register")}
                  className="text-volt hover:text-white transition-colors">Create account</button>
              )}
              {allowRegister && mode === "register" && (
                <button type="button" data-testid="member-show-login" onClick={() => setMode("login")}
                  className="text-volt hover:text-white transition-colors">Sign in</button>
              )}
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}
